import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import {
  TrophyIcon,
  WaitingIcon,
  WarningIcon,
} from '../../assets/svg/SVGComponents';
import frlLogo from '../../assets/images/frl/frl-logo-white.svg';
import frlMascot from '../../assets/images/frl/frl-mascot.svg';
import BoardQrCode, { boardUrl } from '../../components/BoardQrCode';
import CountdownDigits from './components/CountdownDigits';
import FinalPodium from './components/FinalPodium';
import PhaseTakeover from './components/PhaseTakeover';
import RankingBoard from './components/RankingBoard';
import RoundBanner from './components/RoundBanner';
import PresentationPanel from './components/PresentationPanel';
import SchedulePanel from './components/SchedulePanel';
import LiveFooter from './components/LiveFooter';
import useLeaderConfetti from './components/useLeaderConfetti';
import useNow from './hooks/useNow';
import useRowsPerPage from './hooks/useRowsPerPage';
import useSafetyReload from './hooks/useSafetyReload';
import useWakeLock from './hooks/useWakeLock';
import { IBoardSnapshot } from '../../dtos/IBoardSnapshot';
import { wallClockNow } from '../../utils/tournament/presentationAgenda';
import BoardConnection, { TConnectionStatus } from './logic/BoardConnection';
import { initialPresentation, presentationReducer } from './logic/presentation';
import { PODIUM_ROWS, podiums } from './logic/final';
import { boardPhase, countdown } from './logic/schedule';
import {
  BoardPageBackground,
  Body,
  BrandLogo,
  Centered,
  Clock,
  Column,
  Header,
  HeaderDivider,
  HeroMascot,
  IconText,
  LiveDot,
  PhaseTag,
  QrPanel,
  Screen,
  SideStack,
  TitleBlock,
} from './styles';

const PHASE_LABEL = {
  before: 'Aguardando início',
  live: 'Ao vivo',
  after: 'Resultado final',
};

/** Trophy é 32×32: stroke 2 equivale ao 1.5 dos ícones de 24. */
const PHASE_ICON = {
  before: (
    <IconText>
      <WaitingIcon />
    </IconText>
  ),
  live: <LiveDot />,
  after: (
    <IconText>
      <TrophyIcon strokeWidth={2} />
    </IconText>
  ),
};

export interface IBoardSourceHandlers {
  onSnapshot: (snapshot: IBoardSnapshot) => void;
  onStatus: (status: TConnectionStatus) => void;
  onNotFound: () => void;
}

/** De onde vêm as versões do telão; devolve a função que desliga. */
export type TBoardSource = (handlers: IBoardSourceHandlers) => () => void;

const apiSource =
  (token: string): TBoardSource =>
  handlers => {
    const connection = new BoardConnection({
      baseUrl: process.env.REACT_APP_API_URL ?? '',
      token,
      ...handlers,
    });
    connection.start();
    return () => connection.stop();
  };

interface ITournamentBoardProps {
  /** Substitui a API (ex.: demo com torneio simulado). */
  source?: TBoardSource;
}

/**
 * Telão público do torneio (`/telao/:token`): classificação, rodada encerrada
 * e próximas rodadas, atualizados em tempo real. Fica aberto o dia todo numa
 * TV e também abre no celular pelo QR code.
 */
const TournamentBoard: React.FC<ITournamentBoardProps> = ({ source }) => {
  const { token = 'demo' } = useParams<{ token: string }>();
  const [state, dispatch] = useReducer(
    presentationReducer,
    initialPresentation,
  );
  const [status, setStatus] = useState<TConnectionStatus>('connecting');
  const [notFound, setNotFound] = useState(false);
  const now = useNow();
  const rowsPerPage = useRowsPerPage();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(
    () =>
      (source ?? apiSource(token))({
        onSnapshot: snapshot => dispatch({ type: 'receive', snapshot }),
        onStatus: setStatus,
        onNotFound: () => setNotFound(true),
      }),
    [token, source],
  );

  const onBannerDone = useCallback(() => dispatch({ type: 'bannerDone' }), []);

  useWakeLock();
  useSafetyReload(Boolean(state.banner) || state.queue.length > 0, !source);
  useLeaderConfetti(state.effects.newLeaders, state.effectsKey);

  useEffect(() => {
    if (state.latest)
      document.title = `Telão · ${state.latest.tournament.name}`;
  }, [state.latest]);

  if (notFound) {
    return (
      <Screen>
        <BoardPageBackground />
        <Centered style={{ gridRow: '1 / -1' }}>
          <BrandLogo src={frlLogo} alt="Fuzzy Robotics League" />
          {/* Warning é 378×378: stroke 24 equivale ao 1.5 dos ícones de 24. */}
          <WarningIcon
            width="4rem"
            height="4rem"
            fill="none"
            stroke="currentColor"
            strokeWidth={24}
          />
          <h2>Telão não encontrado</h2>
          <p>Confira o link ou o QR code com a organização do torneio.</p>
        </Centered>
      </Screen>
    );
  }

  const { displayed, latest } = state;

  if (!displayed || !latest) {
    return (
      <Screen>
        <BoardPageBackground />
        <Centered style={{ gridRow: '1 / -1' }}>
          <BrandLogo src={frlLogo} alt="Fuzzy Robotics League" />
          <p>
            <IconText>
              <WaitingIcon />
              Carregando o telão…
            </IconText>
          </p>
        </Centered>
      </Screen>
    );
  }

  // Horários do cronograma são hora de parede em UTC: o relógio da tela vai
  // para a mesma escala antes de comparar (o relógio do cabeçalho segue real).
  const nowWall = wallClockNow(now);
  const { phase, firstStart } = boardPhase(latest);
  const timeLeft = countdown(firstStart, nowWall);
  // Abertura de fase segue o que já está na tela: espera a fila de banners.
  const shownPhase = boardPhase(displayed).phase;

  return (
    <Screen>
      <BoardPageBackground />
      <RoundBanner round={state.banner} onDone={onBannerDone} />
      <PhaseTakeover phase={shownPhase} snapshot={displayed} />

      <Header>
        <BrandLogo src={frlLogo} alt="Fuzzy Robotics League" />
        <HeaderDivider />
        <TitleBlock>
          <h1>{latest.tournament.name}</h1>
          <PhaseTag $phase={phase}>
            {PHASE_ICON[phase]}
            {PHASE_LABEL[phase]}
          </PhaseTag>
        </TitleBlock>
        <Clock>
          {new Date(now).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Clock>
      </Header>

      <Body>
        <Column>
          {phase === 'before' ? (
            <Centered>
              <HeroMascot src={frlMascot} alt="" />
              <h2>Prepare os robôs!</h2>
              {timeLeft ? (
                <>
                  <p>A primeira rodada começa em</p>
                  <CountdownDigits value={timeLeft} />
                </>
              ) : (
                <p>As rodadas começam em instantes.</p>
              )}
            </Centered>
          ) : (
            <>
              {/* Tela final fixa: pódio por categoria + classificação (FE-77). */}
              {phase === 'after' && <FinalPodium groups={displayed.ranking} />}
              <RankingBoard
                snapshot={displayed}
                effects={state.effects}
                effectsKey={state.effectsKey}
                focusTeamId={state.banner?.team_id ?? null}
                rowsPerPage={
                  phase === 'after' && podiums(displayed.ranking).length > 0
                    ? Math.max(3, rowsPerPage - PODIUM_ROWS)
                    : rowsPerPage
                }
                isMobile={isMobile}
              />
            </>
          )}
        </Column>

        <Column>
          <SideStack>
            {phase !== 'after' && (
              <SchedulePanel
                upcoming={latest.upcoming}
                now={nowWall}
                started={state.started}
              />
            )}
            {/* Etapa nacional: some sozinho quando não há apresentação. */}
            <PresentationPanel rooms={latest.presentations} nowWall={nowWall} />
          </SideStack>
          {!isMobile && (
            <QrPanel>
              <BoardQrCode url={boardUrl(token)} size={176} />
              <div>
                <h2>Acompanhe pelo celular</h2>
                <p>Aponte a câmera para o QR code</p>
              </div>
            </QrPanel>
          )}
        </Column>
      </Body>

      <LiveFooter rounds={displayed.last_rounds} status={status} />
    </Screen>
  );
};

export default TournamentBoard;
